import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Pre-launch gate for the WEB UI. Hardened: the passcode is verified by the
// BACKEND, which returns a signed token attached to every API request. Without
// it the backend returns 403, so editing the page source can't bypass it. The
// mobile app is exempt via its own client key. Backend /api/docs stay open.
const GATE_TOKEN = "sc_gate_token";

// Routes anyone may reach without the early-access code — password recovery is
// public so locked-out users (and one-tap reset email links) always work. The
// legal/info pages are public so they're crawlable + reusable for app-store /
// AdSense review while the product itself stays gated.
const PUBLIC_PATHS = ["/forgot", "/privacy", "/privacy-policy", "/terms", "/about",
  "/contact", "/support", "/guidelines", "/community-guidelines", "/attribution", "/discover", "/together", "/bari", "/intelligence-hub", "/rewind", "/premiere", "/friends", "/concept/banner-header"];
// Route prefixes anyone may reach without the code (dynamic paths).
const PUBLIC_PREFIXES = ["/join/", "/i/", "/u/", "/profile/", "/list/"];
const isPublicPath = (p) => PUBLIC_PATHS.includes(p) || PUBLIC_PREFIXES.some((pre) => p.startsWith(pre));

export function SiteGate({ children }) {
  const location = useLocation();
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(null); // null = still checking
  const [unlocked, setUnlocked] = useState(() => {
    try { return !!(localStorage.getItem(GATE_TOKEN) || localStorage.getItem("sc_has_session")); } catch { return false; }
  });
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    client.get("/gate/status")
      .then((r) => setEnabled(!!r.data.gate_enabled))
      .catch(() => setEnabled(true)); // fail closed if we can't tell
  }, []);

  // A signed-in user (e.g. an invitee who just used the email-code landing) is
  // already past the gate — reveal the app the moment auth resolves.
  useEffect(() => {
    if (user) setUnlocked(true);
  }, [user]);

  const showingGate = enabled === true && !unlocked && !isPublicPath(location.pathname);

  // Funnel beacon: count each time the Coming soon screen is shown.
  useEffect(() => {
    if (showingGate) client.post("/gate/event", { type: "view" }).catch(() => {});
  }, [showingGate]);

  // Password recovery (and its one-tap email link) is always reachable.
  if (isPublicPath(location.pathname)) return children;
  // Gate is off (e.g. after public launch) → render app straight through.
  if (enabled === false) return children;
  if (enabled === null && !unlocked) {
    return <div className="flex min-h-screen items-center justify-center bg-obsidian" />;
  }
  if (unlocked) return children;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await client.post("/gate/verify", { code: code.trim() });
      if (data.token) {
        try { localStorage.setItem(GATE_TOKEN, data.token); } catch {}
        setUnlocked(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "That code isn't right — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-obsidian px-6" data-testid="site-gate">
      <div className="w-full max-w-md text-center">
        <p className="mb-3 text-[12px] font-bold uppercase tracking-[0.3em] text-gold/70">Screening Circle</p>
        <h1 className="font-display text-5xl font-semibold leading-[0.95] tracking-tight text-[#F6F5F1] sm:text-6xl">
          Coming soon
        </h1>
        <p className="mx-auto mt-4 max-w-sm text-[15px] text-white/50">
          We're putting the finishing touches on the web experience. Have an early-access code?
        </p>

        <form onSubmit={submit} className="mt-8 flex flex-col gap-3">
          <input
            type="password"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(""); }}
            placeholder="Early-access code"
            data-testid="site-gate-input"
            autoFocus
            className="w-full rounded-full border border-white/12 bg-surface px-5 py-3.5 text-center text-[15px] text-[#F6F5F1] placeholder:text-faint focus:border-gold focus:outline-none"
          />
          {error && <p className="text-[13px] font-semibold text-red-400" data-testid="site-gate-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="site-gate-submit"
            className="rounded-full bg-gold px-7 py-3.5 text-[15px] font-extrabold text-obsidian transition-colors hover:bg-gold-light disabled:opacity-60">
            {busy ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>
    </div>
  );
}
